import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  HStack,
} from '@chakra-ui/react'
import { ArrowLeft, RefreshCw } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { TwoFactorService } from '@/lib/services/twoFactorService'
import { z } from 'zod'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { consumePostLoginRedirect } from '@/lib/auth/postLoginRedirect'

// Matches backend: 6-digit email code or 8-char hex backup code (TwoFactorAuthController)
const TWO_FACTOR_CODE_REGEX = /^(\d{6}|[A-Fa-f0-9]{8})$/

function isCompleteTwoFactorCode(value: string): boolean {
  return TWO_FACTOR_CODE_REGEX.test(value)
}

const twoFactorSchema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .refine((s) => TWO_FACTOR_CODE_REGEX.test(s), {
      message:
        'Enter the 6-digit code from your email, or an 8-character backup code (0–9 and A–F).',
    }),
})

type TwoFactorFormData = z.infer<typeof twoFactorSchema>

export function TwoFactorForm() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [userEmail, setUserEmail] = useState('')
  const [attemptCount, setAttemptCount] = useState(0)
  const [maxAttempts] = useState(3)
  const [canResend, setCanResend] = useState(true)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resetTimer, setResetTimer] = useState<ReturnType<typeof setInterval> | null>(null)
  const [lastResetTime, setLastResetTime] = useState<number | null>(null)
  const [totalAttempts, setTotalAttempts] = useState(0) // Track total attempts across resets

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { verifyTwoFactor } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<TwoFactorFormData>({
    resolver: zodResolver(twoFactorSchema),
  })

  const codeValue = watch('code')

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8)
    setValue('code', value)
  }

  useEffect(() => {
    if (codeValue && isCompleteTwoFactorCode(codeValue)) {
      handleSubmit(onSubmit)()
    }
  }, [codeValue, handleSubmit])

  useEffect(() => {
    const email = searchParams.get('email')
    if (email) {
      setUserEmail(email)
      setAttemptCount(0)
      setTotalAttempts(0)
      setCanResend(true)
      setResendCooldown(0)
      setLastResetTime(null)
      if (resetTimer) {
        clearInterval(resetTimer)
        setResetTimer(null)
      }
    }
  }, [searchParams])

  const startCooldownTimer = (minutes: number) => {
    // Clear any existing timer first
    if (resetTimer) {
      clearInterval(resetTimer)
    }
    
    let timeLeft = minutes * 60 // Total seconds
    setResendCooldown(minutes) // Initial display value
    
    const timer = setInterval(() => {
      timeLeft--
      const minutesLeft = Math.ceil(timeLeft / 60)
      setResendCooldown(minutesLeft > 0 ? minutesLeft : 0)

      if (timeLeft <= 0) {
        clearInterval(timer)
        // Reset everything after 5 minutes
        setAttemptCount(0) // Reset counter to 0/3 after 5 minutes
        setTotalAttempts(0) // Also reset total attempts
        setCanResend(true)
        setResendCooldown(0)
        setLastResetTime(null)
        setResetTimer(null)
      }
    }, 1000) // Update every second
    
    setResetTimer(timer)
  }

  const formatTime = (minutes: number) => {
    if (minutes < 1) return 'less than a minute'
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`
  }

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const onSubmit = async (data: TwoFactorFormData) => {
    // Disable verify button if max attempts reached or during cooldown
    if (attemptCount >= maxAttempts || resendCooldown > 0) {
      return
    }

    try {
      setIsLoading(true)
      setError('')
      setMessage('')

      const rememberMe = searchParams.get('remember_me') === '1'
      const result = await verifyTwoFactor(userEmail, data.code, rememberMe)

      if (result.success) {
        // Reset counters on success
        setAttemptCount(0)
        setTotalAttempts(0)
        setLastResetTime(null)
        if (resetTimer) {
          clearInterval(resetTimer as any)
          setResetTimer(null)
        }
        const next = consumePostLoginRedirect() || '/dashboard'
        navigate(next, { replace: true })
      } else {
        setError(result.message || 'Invalid verification code. Please try again.')
        // Increment both counters on failed verification
        const newAttemptCount = attemptCount + 1
        const newTotalAttempts = totalAttempts + 1
        setTotalAttempts(newTotalAttempts)

        if (newAttemptCount >= maxAttempts) {
          // Keep attemptCount at maxAttempts (3) when max attempts reached
          setAttemptCount(maxAttempts)
          setCanResend(false)
          setResendCooldown(5)
          startCooldownTimer(5)
        } else {
          // Ensure attemptCount never exceeds maxAttempts
          setAttemptCount(Math.min(newAttemptCount, maxAttempts))
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid verification code. Please try again.')
      // Increment both counters on failed verification
      const newAttemptCount = attemptCount + 1
      const newTotalAttempts = totalAttempts + 1
      setTotalAttempts(newTotalAttempts)

      if (newAttemptCount >= maxAttempts) {
        // Keep attemptCount at maxAttempts (3) when max attempts reached
        setAttemptCount(maxAttempts)
        setCanResend(false)
        setResendCooldown(5)
        startCooldownTimer(5)
      } else {
        // Ensure attemptCount never exceeds maxAttempts
        setAttemptCount(Math.min(newAttemptCount, maxAttempts))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (!canResend) return

    try {
      setResendLoading(true)
      setError('')
      setMessage('')

      const result = await TwoFactorService.sendCode(userEmail)

      if (result.success) {
        setMessage('New verification code sent to your email.')
        setCountdown(60)

        // Increment total attempts (continues counting: 11, 12, 13, etc.)
        const newTotalAttempts = totalAttempts + 1
        setTotalAttempts(newTotalAttempts)

        // If counter was reset (after 5 minutes), start new cycle from 1
        // Otherwise, increment current cycle, but cap at maxAttempts
        if (attemptCount === 0 && totalAttempts > 0) {
          // Counter was reset after 5 minutes, start new cycle from 1
          setAttemptCount(1)
        } else if (attemptCount >= maxAttempts) {
          // If at max attempts, don't increment further - keep at maxAttempts
          setAttemptCount(maxAttempts)
        } else {
          // Continue current cycle, but ensure it doesn't exceed maxAttempts
          const newAttemptCount = Math.min(attemptCount + 1, maxAttempts)
          setAttemptCount(newAttemptCount)
        }

        // Reset the 5-minute timer if it was running
        if (resetTimer) {
          clearInterval(resetTimer as any)
          setResetTimer(null)
        }
        setLastResetTime(null)

        // Check if we need to disable resend and start 5-minute timer
        const currentAttemptCount = attemptCount === 0 && totalAttempts > 0 ? 1 : attemptCount >= maxAttempts ? maxAttempts : attemptCount + 1
        if (currentAttemptCount >= maxAttempts) {
          setAttemptCount(maxAttempts)
          setCanResend(false)
          setResendCooldown(5)
          startCooldownTimer(5)
        }
      } else {
        setError(result.message || 'Failed to resend code. Please try again.')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend code. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img
                  src={siteSettings.auth_logo || '/assets/img/deltrans-logo.png'}
                  alt={siteSettings.site_name || 'Deltrans Logo'}
                  style={{ maxWidth: '300px', height: 'auto' }}
                />
              </Box>

              <Heading size="lg" color={colors.heading} mb={1}>
                Two-Factor Authentication
              </Heading>

              <Text color={colors.text} fontSize="sm" mb={1}>
                We&apos;ve sent a verification code to
              </Text>
              <Text color={colors.email} fontWeight="medium" fontSize="sm">
                {userEmail}
              </Text>
            </Box>

            {message && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                {message}
              </Alert>
            )}

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Alert status={resendCooldown > 0 ? 'error' : attemptCount > 0 ? 'warning' : 'info'} borderRadius="md">
              <AlertIcon />
              <Box w="full">
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Verification Attempts: {resendCooldown > 0 ? maxAttempts : Math.min(attemptCount, maxAttempts)}/{maxAttempts}
                  </Text>
                  <Box w="100px" h="8px" bg={colors.progressBg} borderRadius="full" overflow="hidden">
                    <Box
                      w={`${Math.min((resendCooldown > 0 ? maxAttempts : Math.min(attemptCount, maxAttempts)) / maxAttempts * 100, 100)}%`}
                      h="100%"
                      bg={resendCooldown > 0 ? 'red.500' : attemptCount > 0 ? 'orange.500' : 'gray.300'}
                      borderRadius="full"
                    />
                  </Box>
                </HStack>
                {resendCooldown > 0 ? (
                  <Text fontSize="xs" color="red.600" fontWeight="medium">
                    Maximum attempts reached! Please wait {formatTime(resendCooldown)} before requesting a new code.
                  </Text>
                ) : attemptCount > 0 ? (
                  <Text fontSize="xs" color={colors.text}>
                    {maxAttempts - attemptCount} attempt{maxAttempts - attemptCount !== 1 ? 's' : ''} remaining
                  </Text>
                ) : (
                  <Text fontSize="xs" color={colors.text}>
                    Enter your verification code to continue
                  </Text>
                )}
              </Box>
            </Alert>

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3} w="100%">
                <FormControl isInvalid={!!errors.code}>
                  <FormLabel>Verification Code</FormLabel>
                  <Input
                    {...register('code')}
                    value={codeValue || ''}
                    onChange={handleCodeChange}
                    placeholder="Enter 6-digit code or backup code"
                    textAlign="center"
                    fontSize="xs"
                    letterSpacing="0.25em"
                    maxLength={8}
                    autoComplete="one-time-code"
                    autoFocus
                  />
                  <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  bg={colors.buttonColor}
                  color="white"
                  _hover={{ bg: 'blue.600' }}
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="Verifying..."
                  isDisabled={attemptCount >= maxAttempts || resendCooldown > 0}
                >
                  Verify Code
                </Button>
              </VStack>
            </Box>

            <VStack spacing={2} w="100%">
              <Text fontSize="sm" color={colors.textSecondary} textAlign="center">
                Didn&apos;t receive the code?
              </Text>

              <Button
                variant="link"
                color="blue.500"
                fontSize="sm"
                onClick={handleResendCode}
                isLoading={resendLoading}
                loadingText="Sending..."
                isDisabled={!canResend || countdown > 0 || resendLoading}
                leftIcon={<RefreshCw size={16} />}
              >
                {!canResend ? `Wait ${formatTime(resendCooldown)}` : countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
              </Button>

              <Button
                variant="ghost"
                color={colors.link}
                fontSize="sm"
                onClick={handleBackToLogin}
                leftIcon={<ArrowLeft size={16} />}
              >
                Back to Login
              </Button>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}

